"use client";
import { useState, useEffect, useRef } from "react";
import Head from "next/head";
import Link from "next/link";

const PACKAGES = [
  {
    id: "pro",
    name: "Schedly Pro",
    description: "Online booking + calendar sync — per location/mo",
    retail: 29,
    wholesale: 20.30,
    items: ["Booking pages", "Calendar sync", "Client notifications", "Analytics dashboard"],
  },
  {
    id: "pro-services",
    name: "Schedly Pro + Services",
    description: "Full service business platform — per location/mo",
    retail: 49,
    wholesale: 34.30,
    popular: true,
    items: ["Everything in Pro", "Point of sale", "Staff management", "Customer portal"],
  },
];

const TIERS = [
  { min: 1, max: 9, name: "Starter", discount: 0 },
  { min: 10, max: 24, name: "Growth", discount: 0.08 },
  { min: 25, max: 49, name: "Scale", discount: 0.15 },
  { min: 50, max: 99, name: "Elite", discount: 0.20 },
  { min: 100, max: 249, name: "Enterprise", discount: 0.25 },
  { min: 250, max: 9999, name: "Premier", discount: 0.35 },
];

function getTier(count: number) {
  return TIERS.find((t) => count >= t.min && count <= t.max) || TIERS[0];
}

function fmt(n: number) {
  const hasCents = n % 1 !== 0;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: hasCents ? 2 : 0, maximumFractionDigits: 2 }).format(n);
}

const FAQ_ITEMS = [
  {
    q: "Do my clients ever see the Schedly name?",
    a: "Never. Your custom domain, your logo, your colors, your email sender domain. Schedly is completely invisible to your clients at every touchpoint.",
  },
  {
    q: "How does wholesale billing work?",
    a: "You pay Schedly at wholesale rates per active location each month, plus $7/mo per additional user beyond the first (30% off the $10 standard rate). You invoice your clients at whatever retail price you choose — we never touch your client relationship or their billing.",
  },
  {
    q: "When do volume discounts kick in?",
    a: "Discounts are applied automatically based on your total active location count at billing time. Growth (10+), Scale (25+), Elite (50+), Enterprise (100+), and Premier (250+) tiers unlock progressively lower wholesale rates — up to 35% off at the Premier level.",
  },
  {
    q: "How long does approval take?",
    a: "We review all applications within 2 business days. Once approved you'll receive API credentials, partner portal access, and an onboarding call scheduled immediately.",
  },
  {
    q: "Is there a minimum commitment or contract?",
    a: "No minimum client count, no annual commitment, no long-term contracts. Start with one client and grow at your own pace. You only pay for active accounts each month.",
  },
  {
    q: "Can I resell AI voice agents to my clients?",
    a: "Yes. AI Voice is available at $0.08/min wholesale. You can bundle it into your packages or offer it as a premium add-on at whatever rate you want — that margin is yours.",
  },
  {
    q: "What support do partners get?",
    a: "All partners get priority ticket support with fast response times, a dedicated onboarding call, a named partner success contact, and access to our private partner Slack.",
  },
  {
    q: "Can I white-label the emails my clients receive?",
    a: "Yes — on the Reseller tier. Booking confirmations, reminders, and receipts all send from your custom domain (e.g. bookings@yourbrand.com) with your logo and no mention of Schedly.",
  },
];

export default function PartnerLandingPage() {
  const [clientCount, setClientCount] = useState(25);
  const [pkgIdx, setPkgIdx] = useState(1);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const pkg = PACKAGES[pkgIdx];
  const tier = getTier(clientCount);
  const effectiveWholesale = pkg.wholesale * (1 - tier.discount);
  const platformFee = 199;
  const monthlyCost = effectiveWholesale * clientCount + platformFee;
  const monthlyRevenue = pkg.retail * clientCount;
  const monthlyProfit = monthlyRevenue - monthlyCost;
  const annualProfit = monthlyProfit * 12;
  const marginPct = Math.round((monthlyProfit / monthlyRevenue) * 100);
  const costBarWidth = Math.min(Math.round((monthlyCost / monthlyRevenue) * 100), 100);
  const sliderPct = Math.round(((clientCount - 1) / 249) * 100);

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: "Schedly",
        url: "https://schedly.io",
        description: "White-label scheduling software for agencies, SaaS companies, and resellers.",
      },
      {
        "@type": "WebPage",
        name: "White-Label Scheduling Software Partner & Reseller Program | Schedly",
        description:
          "Resell or embed Schedly's scheduling platform under your own brand. White-label, wholesale pricing, full API for agencies, SaaS builders, and franchises.",
        url: "https://schedly.io/partner",
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://schedly.io" },
            { "@type": "ListItem", position: 2, name: "Partners", item: "https://schedly.io/partner" },
          ],
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: FAQ_ITEMS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <>
      <Head>
        <title>White-Label Scheduling Software for Resellers & Agencies | Partner Program | Schedly</title>
        <meta
          name="description"
          content="Resell scheduling software under your own brand. Schedly's partner program offers white-label booking platforms, wholesale pricing, and a full scheduling API for agencies, SaaS companies, and franchises. Start earning today."
        />
        <meta
          name="keywords"
          content="white label scheduling software, scheduling software reseller program, booking platform white label, appointment scheduling API, scheduling software for agencies, franchise scheduling platform, embed scheduling in SaaS, wholesale scheduling software, resell booking platform, white label booking system, scheduling software reseller, scheduling platform for partners"
        />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Schedly" />
        <meta name="theme-color" content="#4f46e5" />
        <link rel="canonical" href="https://schedly.io/partner" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://schedly.io/partner" />
        <meta property="og:title" content="White-Label Scheduling Software for Resellers | Schedly Partner Program" />
        <meta
          property="og:description"
          content="Launch your own scheduling platform in days. White-label, wholesale pricing, full API. Built for agencies, SaaS companies, and franchises."
        />
        <meta property="og:image" content="https://schedly.io/og-partner.png" />
        <meta property="og:site_name" content="Schedly" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="White-Label Scheduling Software for Resellers | Schedly" />
        <meta
          name="twitter:description"
          content="Resell scheduling under your brand. Wholesale pricing, full white-label, complete API."
        />
        <meta name="twitter:image" content="https://schedly.io/og-partner.png" />

        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap"
          rel="stylesheet"
        />

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
        <style dangerouslySetInnerHTML={{ __html: `
        * { font-family: 'Inter', system-ui, sans-serif; box-sizing: border-box; }

        .mesh-gradient {
          background:
            radial-gradient(at 40% 20%, rgba(99,102,241,0.13) 0px, transparent 50%),
            radial-gradient(at 80% 0%, rgba(124,58,237,0.09) 0px, transparent 50%),
            radial-gradient(at 0% 50%, rgba(99,102,241,0.09) 0px, transparent 50%),
            radial-gradient(at 80% 50%, rgba(236,72,153,0.06) 0px, transparent 50%),
            radial-gradient(at 0% 100%, rgba(99,102,241,0.08) 0px, transparent 50%),
            linear-gradient(180deg,#fafbff 0%,#ffffff 100%);
        }

        .gradient-text {
          background: linear-gradient(135deg,#111827 0%,#4f46e5 25%,#7c3aed 50%,#db2777 75%,#4f46e5 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          padding-bottom: 0.1em;
          animation: gradient-flow 5s ease infinite;
        }

        @keyframes gradient-flow {
          0%,100% { background-position: 0% center; }
          50% { background-position: 200% center; }
        }

        .blob {
          border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
          animation: blob-morph 8s ease-in-out infinite;
        }

        @keyframes blob-morph {
          0%,100% { border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; }
          25% { border-radius: 58% 42% 75% 25% / 76% 46% 54% 24%; }
          50% { border-radius: 50% 50% 33% 67% / 55% 27% 73% 45%; }
          75% { border-radius: 33% 67% 58% 42% / 63% 68% 32% 37%; }
        }

        .btn-primary {
          position: relative;
          background: linear-gradient(135deg,#6366f1 0%,#4f46e5 100%);
          box-shadow: 0 4px 18px rgba(99,102,241,0.4);
          transition: all 0.3s cubic-bezier(0.175,0.885,0.32,1.275);
          overflow: hidden;
          color: #fff;
          font-weight: 700;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          border: none;
          cursor: pointer;
        }

        .btn-primary::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg,#4338ca 0%,#7c3aed 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .btn-primary:hover {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 8px 28px rgba(99,102,241,0.55);
        }

        .btn-primary:hover::before { opacity: 1; }
        .btn-primary > * { position: relative; z-index: 1; }
        .btn-primary span { position: relative; z-index: 1; }

        .btn-ghost {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #4f46e5;
          font-weight: 600;
          border: 2px solid #e0e7ff;
          border-radius: 12px;
          background: #fff;
          text-decoration: none;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .btn-ghost:hover {
          background: #eef2ff;
          border-color: #c7d2fe;
          transform: translateY(-1px);
        }

        .card-3d {
          transform-style: preserve-3d;
          transition: transform 0.4s cubic-bezier(0.175,0.885,0.32,1.275), box-shadow 0.4s ease;
        }

        .card-3d:hover {
          transform: perspective(1000px) rotateX(2deg) rotateY(-2deg) translateZ(8px);
          box-shadow: 0 20px 60px rgba(99,102,241,0.12);
        }

        .shine {
          position: relative;
          overflow: hidden;
        }

        .shine::after {
          content: '';
          position: absolute;
          top: -50%;
          left: -75%;
          width: 50%;
          height: 200%;
          background: linear-gradient(to right,transparent 0%,rgba(255,255,255,0.4) 50%,transparent 100%);
          transform: skewX(-15deg);
          transition: left 0.6s ease;
        }

        .shine:hover::after { left: 125%; }

        .grain::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
          opacity: 0.025;
          pointer-events: none;
        }

        .float-slow { animation: float-slow 6s ease-in-out infinite; }
        @keyframes float-slow {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }

        .tier-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; border: 1px solid; }
        .tier-Starter { background: #f8fafc; color: #64748b; border-color: #cbd5e1; }
        .tier-Growth { background: #ecfdf5; color: #065f46; border-color: #6ee7b7; }
        .tier-Scale { background: #eff6ff; color: #1d4ed8; border-color: #93c5fd; }
        .tier-Elite { background: #f5f3ff; color: #6d28d9; border-color: #c4b5fd; }
        .tier-Enterprise { background: #fffbeb; color: #92400e; border-color: #fcd34d; }
        .tier-Premier { background: #fdf2f8; color: #9d174d; border-color: #f9a8d4; }

        input[type=range] { -webkit-appearance: none; appearance: none; background: transparent; cursor: pointer; width: 100%; }
        input[type=range]::-webkit-slider-runnable-track { height: 6px; background: #e2e8f0; border-radius: 3px; }
        input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 22px; height: 22px;
          border-radius: 50%;
          background: linear-gradient(135deg,#6366f1,#4f46e5);
          margin-top: -8px;
          border: 3px solid #fff;
          box-shadow: 0 2px 10px rgba(99,102,241,0.45);
        }
        input[type=range]::-moz-range-track { height: 6px; background: #e2e8f0; border-radius: 3px; }
        input[type=range]::-moz-range-thumb {
          width: 22px; height: 22px; border-radius: 50%;
          background: linear-gradient(135deg,#6366f1,#4f46e5);
          border: 3px solid #fff;
          box-shadow: 0 2px 10px rgba(99,102,241,0.45);
        }

        @keyframes count-up { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        .count-anim { animation: count-up 0.3s ease; }

        @media (max-width: 700px) {
          .hero-cta-row { flex-direction: column; align-items: stretch !important; }
          .hero-cta-row a, .hero-cta-row button { text-align: center; justify-content: center; }
          .grid-2col { grid-template-columns: 1fr !important; }
          .grid-4col { grid-template-columns: 1fr 1fr !important; }
          .calc-results { grid-template-columns: 1fr 1fr !important; }
          .stats-row { gap: 20px !important; }
          .stats-row .divider { display: none !important; }
        }
      ` }} />
      </Head>

      <div style={{ background: "#fff", overflowX: "hidden" }}>

        {/* ── NAV ───────────────────────────────────────────────── */}
        <nav style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <a href="https://schedly.io" style={{ display: "flex", alignItems: "center" }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/schedly-logo.png" alt="Schedly" style={{ height: 28, width: "auto" }} />
              </a>
              <span style={{ marginLeft: 6, background: "linear-gradient(135deg,#eef2ff,#f5f3ff)", color: "#4f46e5", border: "1px solid #c7d2fe", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>PARTNERS</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Link href="/partner/login" style={{ color: "#6b7280", fontSize: 14, fontWeight: 600, textDecoration: "none" }}>
                Partner Login
              </Link>
              <Link href="/partner/apply" className="btn-primary" style={{ padding: "9px 20px", fontSize: 14 }}>
                <span>Apply Now</span>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </Link>
            </div>
          </div>
        </nav>

        {/* ── HERO ──────────────────────────────────────────────── */}
        <section className="mesh-gradient grain" style={{ position: "relative", padding: "100px 24px 120px", overflow: "hidden" }}>
          {/* Blob decorations */}
          <div className="blob" style={{ position: "absolute", top: 40, right: "8%", width: 340, height: 340, background: "rgba(99,102,241,0.08)", filter: "blur(60px)", animationDelay: "-2s" }} />
          <div className="blob" style={{ position: "absolute", bottom: 60, left: "5%", width: 280, height: 280, background: "rgba(168,85,247,0.07)", filter: "blur(60px)", animationDelay: "-4s" }} />

          <div style={{ maxWidth: 860, margin: "0 auto", textAlign: "center", position: "relative" }}>
            {/* Badge */}
            <div className="float-slow" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 18px", background: "linear-gradient(135deg,#eef2ff,#f5f3ff)", border: "1px solid rgba(199,210,254,0.8)", borderRadius: 30, marginBottom: 28 }}>
              <svg width="14" height="14" viewBox="0 0 20 20" fill="#6366f1"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <span style={{ color: "#4f46e5", fontSize: 13, fontWeight: 700 }}>Now accepting partner applications</span>
            </div>

            <h1 style={{ fontSize: "clamp(40px,6.5vw,76px)", fontWeight: 900, lineHeight: 1.08, letterSpacing: "-0.035em", color: "#111827", margin: "0 0 24px" }}>
              Turn Scheduling Into{" "}
              <span className="gradient-text">Your Revenue Stream</span>
            </h1>

            <p style={{ color: "#4b5563", fontSize: "clamp(17px,2.2vw,21px)", lineHeight: 1.65, maxWidth: 640, margin: "0 auto 44px", fontWeight: 400 }}>
              Resell or embed a complete scheduling platform under your own brand.{" "}
              <strong style={{ color: "#111827" }}>White-label ready</strong>, wholesale pricing, full API — built for agencies, SaaS companies, and franchises.
            </p>

            <div className="hero-cta-row" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, flexWrap: "wrap" }}>
              <Link href="/partner/apply" className="btn-primary" style={{ padding: "15px 36px", fontSize: 16 }}>
                <span>Apply to Partner Program</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </Link>
              <a href="#calculator" className="btn-ghost" style={{ padding: "15px 28px", fontSize: 15 }}>
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Calculate Your Revenue
              </a>
            </div>

            {/* Stats */}
            <div className="stats-row" style={{ marginTop: 72, display: "flex", alignItems: "center", justifyContent: "center", gap: 0, flexWrap: "wrap" }}>
              {[
                { value: "40%+", label: "Average partner margin" },
                { value: "$0", label: "Setup or onboarding fee" },
                { value: "2 days", label: "Application review" },
                { value: "100%", label: "White-label capable" },
              ].map((s, i) => (
                <div key={i} style={{ padding: "0 36px", display: "flex", flexDirection: "column", alignItems: "center", borderLeft: i === 0 ? "none" : "1px solid #e5e7eb", gap: 4 }}>
                  <div style={{ color: "#111827", fontSize: "clamp(26px,3.5vw,38px)", fontWeight: 900, letterSpacing: "-0.03em" }}>{s.value}</div>
                  <div style={{ color: "#6b7280", fontSize: 12, fontWeight: 600 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHO IT'S FOR ──────────────────────────────────────── */}
        <section style={{ background: "#fff", padding: "88px 24px", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <p style={{ color: "#4f46e5", fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Built for</p>
              <h2 style={{ color: "#111827", fontSize: "clamp(26px,4vw,44px)", fontWeight: 900, letterSpacing: "-0.025em", margin: "0 0 14px" }}>
                Every Kind of Scheduling Reseller
              </h2>
              <p style={{ color: "#6b7280", fontSize: 18, maxWidth: 520, margin: "0 auto", lineHeight: 1.6 }}>
                Whether you build SaaS, run an agency, or manage a franchise — Schedly powers your scheduling layer invisibly.
              </p>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 20 }}>
              {[
                {
                  gradient: "linear-gradient(135deg,#6366f1,#818cf8)",
                  glow: "rgba(99,102,241,0.15)",
                  icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="9,22 9,12 15,12 15,22" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
                  title: "Digital Agencies",
                  desc: "Add recurring scheduling MRR to client retainers. White-label under your agency brand — clients never know it's Schedly.",
                  tag: "Most common",
                  stat: "40%+ avg margin",
                },
                {
                  gradient: "linear-gradient(135deg,#7c3aed,#a78bfa)",
                  glow: "rgba(124,58,237,0.15)",
                  icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M16 18l6-6-6-6M8 6l-6 6 6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
                  title: "SaaS Builders",
                  desc: "Embed a complete booking system into your product via our REST API. Ship scheduling in days, not months of dev time.",
                  tag: "API-first",
                  stat: "Full API access",
                },
                {
                  gradient: "linear-gradient(135deg,#0891b2,#38bdf8)",
                  glow: "rgba(8,145,178,0.12)",
                  icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 21h18M9 21V9l6-4v16M9 9H3M9 9l6-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
                  title: "Franchise Groups",
                  desc: "Centrally manage scheduling for all locations. Consistent branding, consolidated reporting, per-location control.",
                  tag: "Multi-location",
                  stat: "Unlimited locations",
                },
                {
                  gradient: "linear-gradient(135deg,#059669,#34d399)",
                  glow: "rgba(5,150,105,0.12)",
                  icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="9" cy="7" r="4" stroke="#fff" strokeWidth="2"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
                  title: "Consultants & VARs",
                  desc: "Resell as a standalone product. You set the price, own the relationship, collect the margin. We run the platform.",
                  tag: "High margin",
                  stat: "Up to 72% margin",
                },
              ].map((c) => (
                <div key={c.title} className="card-3d" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 22, padding: "28px 26px", position: "relative", cursor: "default", overflow: "hidden" }}>
                  {/* Gradient glow top */}
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: c.gradient, borderRadius: "22px 22px 0 0" }} />
                  <div style={{ position: "absolute", top: 18, right: 18, background: "#f8fafc", color: "#6b7280", fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, border: "1px solid #e5e7eb" }}>{c.tag}</div>

                  {/* Icon with gradient bg */}
                  <div style={{ width: 52, height: 52, borderRadius: 16, background: c.gradient, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18, boxShadow: `0 8px 20px ${c.glow}` }}>
                    {c.icon}
                  </div>

                  <h3 style={{ color: "#111827", fontSize: 17, fontWeight: 800, margin: "0 0 8px", letterSpacing: "-0.01em" }}>{c.title}</h3>
                  <p style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.65, margin: "0 0 18px" }}>{c.desc}</p>

                  {/* Stat chip */}
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", background: c.glow, borderRadius: 20 }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: c.gradient }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>{c.stat}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── REVENUE CALCULATOR ────────────────────────────────── */}
        <section id="calculator" className="mesh-gradient grain" style={{ position: "relative", padding: "96px 24px", borderBottom: "1px solid #e5e7eb", overflow: "hidden" }}>
          <div className="blob" style={{ position: "absolute", top: "30%", right: "-5%", width: 300, height: 300, background: "rgba(99,102,241,0.06)", filter: "blur(60px)" }} />

          <div style={{ maxWidth: 1000, margin: "0 auto", position: "relative" }}>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <p style={{ color: "#4f46e5", fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Revenue Calculator</p>
              <h2 style={{ color: "#111827", fontSize: "clamp(26px,4vw,44px)", fontWeight: 900, letterSpacing: "-0.025em", margin: "0 0 14px" }}>
                See Exactly What You'll <span className="gradient-text">Earn</span>
              </h2>
              <p style={{ color: "#6b7280", fontSize: 18, maxWidth: 460, margin: "0 auto" }}>
                Drag the slider to model your client count. Watch your profit scale in real time.
              </p>
            </div>

            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 24, overflow: "hidden", boxShadow: "0 20px 80px rgba(99,102,241,0.08), 0 4px 16px rgba(0,0,0,0.04)" }}>

              {/* Controls */}
              <div style={{ padding: "36px 40px", borderBottom: "1px solid #f1f5f9" }}>

                {/* Package selector */}
                <div style={{ marginBottom: 32 }}>
                  <div style={{ color: "#374151", fontSize: 13, fontWeight: 700, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>Package you resell to clients</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10 }}>
                    {PACKAGES.map((p, i) => (
                      <button
                        key={p.id}
                        onClick={() => setPkgIdx(i)}
                        style={{
                          padding: "16px 16px",
                          border: pkgIdx === i ? "2px solid #6366f1" : "2px solid #e2e8f0",
                          borderRadius: 14,
                          background: pkgIdx === i ? "#eef2ff" : "#fafafa",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.18s",
                          position: "relative",
                          boxShadow: pkgIdx === i ? "0 4px 12px rgba(99,102,241,0.15)" : "none",
                        }}>
                        {p.popular && (
                          <div style={{ position: "absolute", top: -1, right: 12, background: "linear-gradient(135deg,#6366f1,#7c3aed)", color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 9px", borderRadius: "0 0 6px 6px", letterSpacing: "0.08em" }}>POPULAR</div>
                        )}
                        <div style={{ color: "#111827", fontSize: 15, fontWeight: 800 }}>{p.name}</div>
                        <div style={{ color: "#6b7280", fontSize: 11, marginTop: 2 }}>{p.description}</div>
                        <div style={{ marginTop: 10, display: "flex", alignItems: "baseline", gap: 6 }}>
                          <span style={{ color: "#4f46e5", fontSize: 18, fontWeight: 900 }}>{fmt(p.retail)}</span>
                          <span style={{ color: "#9ca3af", fontSize: 11 }}>/mo retail</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Slider */}
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <span style={{ color: "#374151", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Number of clients</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ color: "#111827", fontSize: 32, fontWeight: 900, letterSpacing: "-0.03em" }}>{clientCount}</span>
                      <span className={`tier-badge tier-${tier.name}`}>
                        {tier.name}
                        {tier.discount > 0 && <span style={{ opacity: 0.7 }}>· {Math.round(tier.discount * 100)}% off wholesale</span>}
                      </span>
                    </div>
                  </div>

                  <div style={{ position: "relative", height: 28, display: "flex", alignItems: "center" }}>
                    <div style={{ position: "absolute", left: 0, right: 0, height: 6, background: "#e2e8f0", borderRadius: 3 }} />
                    <div style={{ position: "absolute", left: 0, width: `${sliderPct}%`, height: 6, background: "linear-gradient(90deg,#6366f1,#7c3aed)", borderRadius: 3, transition: "width 0.1s" }} />
                    <input
                      type="range"
                      min={1}
                      max={250}
                      value={clientCount}
                      onChange={(e) => setClientCount(Number(e.target.value))}
                      style={{ position: "relative", zIndex: 2, width: "100%", height: 28 }}
                    />
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                    {TIERS.map((t) => (
                      <span key={t.name} style={{ color: clientCount >= t.min ? "#6366f1" : "#d1d5db", fontSize: 11, fontWeight: 700, transition: "color 0.2s" }}>{t.name}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Results — dark panel */}
              <div style={{ background: "#0f172a", padding: "36px 40px" }}>

                <div className="calc-results" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 20, marginBottom: 32 }}>
                  {[
                    { label: "Monthly Revenue", value: fmt(monthlyRevenue), sub: `${clientCount} × ${fmt(pkg.retail)}`, color: "#a5b4fc" },
                    { label: "Your Cost", value: fmt(monthlyCost), sub: `${fmt(Math.round(effectiveWholesale * 100) / 100)}/client + $199 fee`, color: "#f87171" },
                    { label: "Monthly Profit", value: fmt(monthlyProfit), sub: `${marginPct}% margin`, color: "#34d399" },
                    { label: "Annual Profit", value: fmt(annualProfit), sub: "full-year projection", color: "#fbbf24" },
                  ].map((s) => (
                    <div key={s.label} style={{ textAlign: "center" }}>
                      <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>{s.label}</div>
                      <div key={s.value} className="count-anim" style={{ color: s.color, fontSize: "clamp(18px,2.5vw,28px)", fontWeight: 900, letterSpacing: "-0.02em" }}>{s.value}</div>
                      <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 11, marginTop: 4 }}>{s.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Visual bar */}
                <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 14, padding: "20px 24px", marginBottom: 28 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                    <span style={{ color: "rgba(255,255,255,0.45)", fontSize: 12, fontWeight: 600 }}>Revenue breakdown</span>
                    <span style={{ color: "#34d399", fontSize: 13, fontWeight: 800 }}>{marginPct}% gross margin</span>
                  </div>
                  <div style={{ height: 10, background: "rgba(255,255,255,0.08)", borderRadius: 5, overflow: "hidden" }}>
                    <div style={{ height: "100%", display: "flex" }}>
                      <div style={{ width: `${costBarWidth}%`, background: "linear-gradient(90deg,#ef4444,#f87171)", transition: "width 0.3s ease" }} />
                      <div style={{ flex: 1, background: "linear-gradient(90deg,#059669,#34d399)" }} />
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: "#f87171" }} />
                      <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>Cost {fmt(monthlyCost)}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: "#34d399" }} />
                      <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>Profit {fmt(monthlyProfit)}</span>
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center" }}>
                  <Link href="/partner/apply" className="btn-primary" style={{ padding: "14px 36px", fontSize: 15, boxShadow: "0 0 40px rgba(99,102,241,0.35)" }}>
                    <span>Apply and start earning {fmt(annualProfit)}/year</span>
                    <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── WHOLESALE RATE TABLE ──────────────────────────────── */}
        <section style={{ background: "#fff", padding: "88px 24px", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 52 }}>
              <p style={{ color: "#4f46e5", fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Transparent Wholesale Rates</p>
              <h2 style={{ color: "#111827", fontSize: "clamp(24px,4vw,42px)", fontWeight: 900, letterSpacing: "-0.025em", margin: "0 0 14px" }}>
                You See Exactly What You Pay Us
              </h2>
              <p style={{ color: "#6b7280", fontSize: 17 }}>
                Set your own retail prices. Keep the difference. Volume discounts stack automatically.
              </p>
            </div>

            {/* Volume tiers */}
            <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 36 }}>
              {TIERS.map((t) => (
                <div key={t.name} className="card-3d" style={{ padding: "12px 20px", border: "1px solid #e5e7eb", borderRadius: 12, textAlign: "center", minWidth: 108, background: "#fafafa", cursor: "default" }}>
                  <div style={{ fontWeight: 800, color: "#111827", fontSize: 14 }}>{t.name}</div>
                  <div style={{ color: "#6b7280", fontSize: 11, marginTop: 2 }}>{t.max >= 9999 ? `${t.min}+ clients` : `${t.min}–${t.max} clients`}</div>
                  <div style={{ color: t.discount > 0 ? "#059669" : "#9ca3af", fontWeight: 800, fontSize: 14, marginTop: 6 }}>
                    {t.discount > 0 ? `-${Math.round(t.discount * 100)}% off` : "Standard"}
                  </div>
                </div>
              ))}
            </div>

            {/* Table */}
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 18, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
                    <th style={{ padding: "14px 20px", textAlign: "left", color: "#374151", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}>Product</th>
                    <th style={{ padding: "14px 20px", textAlign: "right", color: "#374151", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}>End-user retail</th>
                    <th style={{ padding: "14px 20px", textAlign: "right", color: "#4f46e5", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}>Your wholesale</th>
                    <th style={{ padding: "14px 20px", textAlign: "right", color: "#059669", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}>Base margin</th>
                    <th style={{ padding: "14px 20px", textAlign: "left", color: "#374151", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { product: "Schedly Pro — per location/mo", retail: "$29", wholesale: "$15", margin: "48%", note: "Includes 1 user" },
                    { product: "Schedly Pro + Services — per location/mo", retail: "$49", wholesale: "$25", margin: "49%", note: "Includes 1 user" },
                    { product: "Additional users — per user/mo", retail: "Your price", wholesale: "$12", margin: "—", note: "Per extra staff user" },
                    { product: "Reserve with Google — per location/mo", retail: "$45", wholesale: "$25", margin: "44%", note: "" },
                    { product: "AI Voice Agent — per minute", retail: "$0.29", wholesale: "$0.08", margin: "72%", note: "" },
                    { product: "Remove Branding — per account/mo", retail: "$19", wholesale: "$12", margin: "37%", note: "" },
                    { product: "SMS Credits — 500 pack", retail: "$20", wholesale: "$12", margin: "40%", note: "" },
                  ].map((r, i) => (
                    <tr key={r.product} style={{ borderBottom: i < 6 ? "1px solid #f1f5f9" : "none", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                      <td style={{ padding: "14px 20px", color: "#111827", fontSize: 14, fontWeight: 500 }}>{r.product}</td>
                      <td style={{ padding: "14px 20px", textAlign: "right", color: "#6b7280", fontSize: 14 }}>{r.retail}</td>
                      <td style={{ padding: "14px 20px", textAlign: "right", color: "#4f46e5", fontSize: 14, fontWeight: 800 }}>{r.wholesale}</td>
                      <td style={{ padding: "14px 20px", textAlign: "right" }}>
                        <span style={{ background: r.margin === "—" ? "#f8fafc" : "#ecfdf5", color: r.margin === "—" ? "#9ca3af" : "#065f46", fontWeight: 800, fontSize: 12, padding: "4px 10px", borderRadius: 8 }}>{r.margin}</span>
                      </td>
                      <td style={{ padding: "14px 20px", color: "#9ca3af", fontSize: 12 }}>{r.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ color: "#9ca3af", fontSize: 12, textAlign: "center", marginTop: 12 }}>
              Volume tier discounts apply on top of these rates automatically at billing time.
            </p>
          </div>
        </section>

        {/* ── PARTNER PLAN ──────────────────────────────────────── */}
        <section id="tiers" className="mesh-gradient grain" style={{ position: "relative", padding: "88px 24px", borderBottom: "1px solid #e5e7eb", overflow: "hidden" }}>
          <div style={{ maxWidth: 520, margin: "0 auto", position: "relative" }}>
            <div style={{ textAlign: "center", marginBottom: 52 }}>
              <p style={{ color: "#4f46e5", fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>The Plan</p>
              <h2 style={{ color: "#111827", fontSize: "clamp(24px,4vw,42px)", fontWeight: 900, letterSpacing: "-0.025em", margin: 0 }}>
                One Program, Everything Included
              </h2>
            </div>

            <div className="card-3d" style={{ background: "linear-gradient(135deg,#f5f3ff,#eef2ff)", border: "2px solid #a5b4fc", borderRadius: 22, padding: "40px", display: "flex", flexDirection: "column", position: "relative", boxShadow: "0 8px 40px rgba(99,102,241,0.15)", cursor: "default" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: "linear-gradient(135deg,#6366f1,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, boxShadow: "0 8px 24px rgba(99,102,241,0.3)" }}>🎨</div>
                <div>
                  <div style={{ color: "#111827", fontWeight: 800, fontSize: 20 }}>White-Label Reseller</div>
                  <div style={{ color: "#6b7280", fontSize: 13 }}>Your brand, your pricing, your business</div>
                </div>
              </div>
              <div style={{ margin: "24px 0", paddingBottom: 24, borderBottom: "1px solid #c7d2fe" }}>
                <span style={{ color: "#111827", fontSize: 44, fontWeight: 900 }}>$199</span>
                <span style={{ color: "#6b7280", fontSize: 15 }}> /month platform fee</span>
                <div style={{ color: "#6b7280", fontSize: 13, marginTop: 6 }}>+ wholesale rates per active location</div>
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 32px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 24px" }}>
                {[
                  "Full REST API access",
                  "Custom domain (yourapp.com)",
                  "Webhook notifications",
                  "Custom logo & brand colors",
                  "Client management portal",
                  "White-label email templates",
                  "Bulk location import",
                  "White-label support email",
                  "Priority partner support",
                  "Dedicated onboarding call",
                  "Named success contact",
                  "Private partner Slack",
                ].map((f) => (
                  <li key={f} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151" }}>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#ddd6fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="10" height="10" viewBox="0 0 20 20" fill="#4f46e5"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                    </div>
                    {f}
                  </li>
                ))}
              </ul>
              <Link href="/partner/apply" className="btn-primary" style={{ justifyContent: "center", padding: "15px", marginTop: "auto" }}>
                <span>Apply as Reseller Partner</span>
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </Link>
            </div>
          </div>
        </section>

        {/* ── FEATURES ──────────────────────────────────────────── */}
        <section style={{ background: "#fff", padding: "88px 24px", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <p style={{ color: "#4f46e5", fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>What You Get</p>
              <h2 style={{ color: "#111827", fontSize: "clamp(24px,4vw,42px)", fontWeight: 900, letterSpacing: "-0.025em", margin: 0 }}>
                A Complete Scheduling Business in a Box
              </h2>
            </div>

            {/* Bento grid — 2 hero cards top, 4 smaller below */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
              {/* Hero card 1 — API */}
              <div className="card-3d" style={{ background: "linear-gradient(135deg,#0f0c29,#1a1040,#302b63)", borderRadius: 22, padding: "36px", cursor: "default", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: "30%", right: "-10%", width: 200, height: 200, background: "rgba(99,102,241,0.2)", borderRadius: "50%", filter: "blur(40px)" }} />
                <div style={{ width: 50, height: 50, borderRadius: 14, background: "rgba(99,102,241,0.25)", border: "1px solid rgba(165,180,252,0.2)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M16 18l6-6-6-6M8 6l-6 6 6 6" stroke="#a5b4fc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <h3 style={{ color: "#fff", fontSize: 22, fontWeight: 900, margin: "0 0 10px", letterSpacing: "-0.02em" }}>Full REST API</h3>
                <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 14, lineHeight: 1.7, margin: "0 0 20px" }}>Complete programmatic control over locations, services, bookings, customers, invoices, and staff. Webhooks for every event in real time.</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {["REST API", "Webhooks", "SDKs"].map((t) => (
                    <span key={t} style={{ background: "rgba(165,180,252,0.12)", color: "#a5b4fc", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 8, border: "1px solid rgba(165,180,252,0.15)" }}>{t}</span>
                  ))}
                </div>
              </div>

              {/* Hero card 2 — White-label */}
              <div className="card-3d" style={{ background: "linear-gradient(135deg,#4f46e5,#7c3aed)", borderRadius: 22, padding: "36px", cursor: "default", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", bottom: "-10%", left: "20%", width: 180, height: 180, background: "rgba(255,255,255,0.08)", borderRadius: "50%", filter: "blur(30px)" }} />
                <div style={{ width: 50, height: 50, borderRadius: 14, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#fff" strokeWidth="2"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <h3 style={{ color: "#fff", fontSize: 22, fontWeight: 900, margin: "0 0 10px", letterSpacing: "-0.02em" }}>True White-Label</h3>
                <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 14, lineHeight: 1.7, margin: "0 0 20px" }}>Your domain, your logo, your colors, your email sender domain. Clients never see the Schedly name — anywhere, ever, at any touchpoint.</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {["Custom domain", "Custom email", "No Schedly branding"].map((t) => (
                    <span key={t} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 8 }}>{t}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* 4 smaller feature cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 18 }}>
              {[
                { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="9" cy="7" r="4" stroke="#4f46e5" strokeWidth="2"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>, iconBg: "#eef2ff", title: "Multi-Client Portal", desc: "Manage all clients from one dashboard. Control feature flags, billing, and access per account.", tags: ["Client management", "Feature flags"] },
                { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 2a10 10 0 100 20 10 10 0 000-20zM12 8v4l3 3" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 12H4M20 12h-4M12 4V2M12 22v-2" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round"/></svg>, iconBg: "#f5f3ff", title: "AI Voice Agent", desc: "Offer automated AI phone booking. $0.08/min wholesale — massive upside for upselling.", tags: ["AI phone agent", "$0.08/min wholesale"] },
                { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="14" rx="2" ry="2" stroke="#059669" strokeWidth="2"/><line x1="8" y1="21" x2="16" y2="21" stroke="#059669" strokeWidth="2" strokeLinecap="round"/><line x1="12" y1="17" x2="12" y2="21" stroke="#059669" strokeWidth="2" strokeLinecap="round"/></svg>, iconBg: "#ecfdf5", title: "Full Service Suite", desc: "Invoicing, POS, inventory, customer portal, staff management — not just booking links.", tags: ["Invoicing", "POS", "Customer portal"] },
                { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke="#0891b2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="10" r="3" stroke="#0891b2" strokeWidth="2"/></svg>, iconBg: "#ecfeff", title: "Reserve with Google", desc: "Book from Google Search & Maps. Enterprise-grade distribution at your wholesale price.", tags: ["Google Search", "Google Maps"] },
              ].map((f) => (
                <div key={f.title} className="card-3d shine" style={{ border: "1px solid #e5e7eb", borderRadius: 18, padding: "24px", background: "#fff", cursor: "default" }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: f.iconBg, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                    {f.icon}
                  </div>
                  <h3 style={{ color: "#111827", fontSize: 16, fontWeight: 800, margin: "0 0 8px" }}>{f.title}</h3>
                  <p style={{ color: "#6b7280", fontSize: 13, lineHeight: 1.65, margin: "0 0 14px" }}>{f.desc}</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {f.tags.map((t) => (
                      <span key={t} style={{ background: "#f8fafc", color: "#6b7280", fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 6, border: "1px solid #e5e7eb" }}>{t}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ──────────────────────────────────────── */}
        <section style={{ background: "#fafbff", padding: "88px 24px", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ maxWidth: 860, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <p style={{ color: "#4f46e5", fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Get Started</p>
              <h2 style={{ color: "#111827", fontSize: "clamp(24px,4vw,42px)", fontWeight: 900, letterSpacing: "-0.025em", margin: 0 }}>
                Live in 3 Steps
              </h2>
            </div>

            {/* Connected step layout */}
            <div style={{ position: "relative" }}>
              {/* Connector line behind cards (desktop only) */}
              <div style={{ position: "absolute", top: 46, left: "calc(16.6% + 8px)", right: "calc(16.6% + 8px)", height: 2, background: "linear-gradient(90deg,#6366f1,#a5b4fc,#6366f1)", zIndex: 0, borderRadius: 2 }} className="step-connector" />

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 32, position: "relative", zIndex: 1 }} className="grid-3col">
                {[
                  {
                    n: "01", title: "Apply",
                    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><polyline points="14,2 14,8 20,8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="16" y1="13" x2="8" y2="13" stroke="#fff" strokeWidth="2" strokeLinecap="round"/><line x1="16" y1="17" x2="8" y2="17" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>,
                    desc: "Submit in 5 minutes. We review within 2 business days and send API credentials on approval.",
                    time: "2 days to approval",
                    gradient: "linear-gradient(135deg,#6366f1,#4f46e5)",
                  },
                  {
                    n: "02", title: "Brand It",
                    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke="#fff" strokeWidth="2"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14M12 1v3M12 20v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M1 12h3M20 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>,
                    desc: "Connect your domain, upload your logo, set your colors. White-label live in under an hour.",
                    time: "< 1 hour to live",
                    gradient: "linear-gradient(135deg,#7c3aed,#6366f1)",
                  },
                  {
                    n: "03", title: "Earn",
                    icon: <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><line x1="12" y1="1" x2="12" y2="23" stroke="#fff" strokeWidth="2" strokeLinecap="round"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
                    desc: "Onboard clients through your portal. Set retail prices. Collect margin every month automatically.",
                    time: "Recurring MRR",
                    gradient: "linear-gradient(135deg,#059669,#10b981)",
                  },
                ].map((s, idx) => (
                  <div key={s.n} style={{ textAlign: "center", cursor: "default" }}>
                    {/* Step icon with number */}
                    <div style={{ position: "relative", display: "inline-block", marginBottom: 24 }}>
                      <div style={{ width: 72, height: 72, borderRadius: 22, background: s.gradient, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 24px rgba(99,102,241,0.3)", margin: "0 auto" }}>
                        {s.icon}
                      </div>
                      <div style={{ position: "absolute", top: -6, right: -6, width: 22, height: 22, borderRadius: "50%", background: "#fff", border: "2px solid #6366f1", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ color: "#6366f1", fontSize: 10, fontWeight: 800 }}>{idx + 1}</span>
                      </div>
                    </div>

                    {/* Arrow between steps (desktop) */}
                    <div style={{ color: "#a5b4fc", fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", marginBottom: 8 }}>{s.n}</div>
                    <h3 style={{ color: "#111827", fontSize: 20, fontWeight: 900, margin: "0 0 10px", letterSpacing: "-0.01em" }}>{s.title}</h3>
                    <p style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.65, margin: "0 0 14px" }}>{s.desc}</p>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", background: "#eef2ff", borderRadius: 20 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#6366f1" }} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#4f46e5" }}>{s.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <style dangerouslySetInnerHTML={{ __html: `@media (max-width: 700px) { .step-connector { display: none !important; } }` }} />
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────── */}
        <section style={{ background: "#fff", padding: "88px 24px", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ maxWidth: 740, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 52 }}>
              <p style={{ color: "#4f46e5", fontSize: 13, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>FAQ</p>
              <h2 style={{ color: "#111827", fontSize: "clamp(24px,4vw,42px)", fontWeight: 900, letterSpacing: "-0.025em", margin: 0 }}>
                Common Questions
              </h2>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {FAQ_ITEMS.map((f, i) => (
                <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden", transition: "box-shadow 0.2s", boxShadow: openFaq === i ? "0 4px 16px rgba(99,102,241,0.1)" : "none" }}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 22px", background: openFaq === i ? "#fafafa" : "#fff", border: "none", cursor: "pointer", textAlign: "left", gap: 16 }}>
                    <span style={{ color: "#111827", fontSize: 15, fontWeight: 700 }}>{f.q}</span>
                    <div style={{ width: 26, height: 26, borderRadius: "50%", background: openFaq === i ? "#eef2ff" : "#f8fafc", border: `1px solid ${openFaq === i ? "#c7d2fe" : "#e5e7eb"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.2s" }}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: openFaq === i ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
                        <path d="M2 4l4 4 4-4" stroke={openFaq === i ? "#4f46e5" : "#9ca3af"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div style={{ padding: "0 22px 20px", borderTop: "1px solid #f1f5f9" }}>
                      <p style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.75, margin: "16px 0 0" }}>{f.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ───────────────────────────────────────────────── */}
        <section className="mesh-gradient grain" style={{ position: "relative", padding: "100px 24px", overflow: "hidden" }}>
          <div className="blob" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 400, background: "rgba(99,102,241,0.08)", filter: "blur(80px)" }} />
          <div style={{ maxWidth: 640, margin: "0 auto", textAlign: "center", position: "relative" }}>
            <div style={{ width: 70, height: 70, borderRadius: 22, background: "linear-gradient(135deg,#6366f1,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px", boxShadow: "0 12px 40px rgba(99,102,241,0.35)", fontSize: 32 }}>🚀</div>
            <h2 style={{ color: "#111827", fontSize: "clamp(28px,5vw,54px)", fontWeight: 900, letterSpacing: "-0.03em", margin: "0 0 18px", lineHeight: 1.1 }}>
              Ready to Add a Scheduling{" "}
              <span className="gradient-text">Revenue Stream?</span>
            </h2>
            <p style={{ color: "#6b7280", fontSize: 18, lineHeight: 1.65, margin: "0 0 40px" }}>
              Apply in 5 minutes. Approved in 2 days. Earning on your first client within a week.
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, flexWrap: "wrap" }}>
              <Link href="/partner/apply" className="btn-primary" style={{ padding: "15px 40px", fontSize: 16 }}>
                <span>Apply to Partner Program</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </Link>
              <Link href="/partner/login" className="btn-ghost" style={{ padding: "15px 28px", fontSize: 15 }}>
                Partner Login
              </Link>
            </div>
          </div>
        </section>

        {/* ── FOOTER ────────────────────────────────────────────── */}
        <footer style={{ background: "#0f172a", padding: "64px 24px 0" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>

            {/* Top row — logo + tagline + socials */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 32, marginBottom: 52, paddingBottom: 48, borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
              <div style={{ maxWidth: 280 }}>
                <a href="https://schedly.io" style={{ display: "inline-block", marginBottom: 16 }}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src="/schedly-logo-white.png" alt="Schedly" style={{ height: 26, width: "auto", opacity: 0.92 }} />
                </a>
                <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, lineHeight: 1.65, margin: "0 0 20px" }}>
                  The scheduling platform built for service businesses — and the partners who power them.
                </p>
                {/* Social links */}
                <div style={{ display: "flex", gap: 10 }}>
                  {[
                    { label: "X / Twitter", href: "https://twitter.com/schedly", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.259 5.631zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
                    { label: "LinkedIn", href: "https://linkedin.com/company/schedly", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg> },
                    { label: "YouTube", href: "https://youtube.com/@schedly", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 00-1.95 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/></svg> },
                  ].map((s) => (
                    <a key={s.label} href={s.href} aria-label={s.label} target="_blank" rel="noopener noreferrer" style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.4)", textDecoration: "none", transition: "all 0.15s" }}>
                      {s.icon}
                    </a>
                  ))}
                </div>
              </div>

              {/* Link columns */}
              <div style={{ display: "flex", gap: 48, flexWrap: "wrap" }}>
                {[
                  {
                    heading: "Product",
                    links: [
                      ["Features", "https://schedly.io/features"],
                      ["Pricing", "https://schedly.io/pricing"],
                      ["Integrations", "https://schedly.io/integrations"],
                      ["AI Voice Agent", "https://schedly.io/ai-voice"],
                      ["Reserve with Google", "https://schedly.io/reserve-with-google"],
                    ],
                  },
                  {
                    heading: "Solutions",
                    links: [
                      ["Digital Agencies", "https://schedly.io/solutions/agencies"],
                      ["SaaS Builders", "https://schedly.io/solutions/saas"],
                      ["Franchise Groups", "https://schedly.io/solutions/franchise"],
                      ["Consultants & VARs", "https://schedly.io/solutions/consultants"],
                      ["Partner Program", "/partner"],
                    ],
                  },
                  {
                    heading: "Resources",
                    links: [
                      ["Documentation", "https://docs.schedly.io"],
                      ["API Reference", "https://docs.schedly.io/api"],
                      ["Help Center", "https://help.schedly.io"],
                      ["Blog", "https://schedly.io/blog"],
                      ["What's New", "https://schedly.io/changelog"],
                    ],
                  },
                  {
                    heading: "Company",
                    links: [
                      ["About", "https://schedly.io/about"],
                      ["Log In", "https://app.schedly.io/login"],
                      ["Get Started Free", "https://schedly.io/signup"],
                      ["Privacy Policy", "/privacy"],
                      ["Terms of Service", "/terms"],
                    ],
                  },
                ].map((col) => (
                  <div key={col.heading}>
                    <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 12, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 16 }}>{col.heading}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {col.links.map(([label, href]) => (
                        <a key={label} href={href} style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, textDecoration: "none", fontWeight: 500, transition: "color 0.15s" }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.85)")}
                          onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.4)")}>
                          {label}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom bar */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, padding: "20px 0", borderTop: "none" }}>
              <div style={{ color: "rgba(255,255,255,0.25)", fontSize: 12 }}>
                © {new Date().getFullYear()} Schedly. All rights reserved.
              </div>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                {[["Privacy", "/privacy"], ["Terms", "/terms"], ["SMS Policy", "/legal/sms-consent-policy"], ["Schedly.io", "https://schedly.io"]].map(([label, href]) => (
                  <a key={label} href={href} style={{ color: "rgba(255,255,255,0.25)", fontSize: 12, textDecoration: "none" }}>{label}</a>
                ))}
              </div>
            </div>

          </div>
        </footer>
      </div>
    </>
  );
}
